import { Pagination, PaginationResponse, User, UserCreatePayload, UserUpdatePayload } from '@ekkogmbh/apisdk';
import { Chip, Divider, Stack, Tooltip, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import moment, { Moment } from 'moment';
import { enqueueSnackbar } from 'notistack';
import { useCallback, useMemo, useState } from 'react';
import { GenericDialog } from '../../Common/Components/GenericDialog';
import { DataTable, dataTableHighlight } from '../../Common/Components/DataTable';
import { AuthMechanisms } from '../../Common/Helper/AuthMechanisms';
import Permissions from 'src/Common/Permissions';
import { UserPanel } from './UserPanel';
import { ContentControl } from 'src/Common/Components/ContentControl';
import { Add, Delete, Edit, SmartToyOutlined, SupervisedUserCircle } from '@mui/icons-material';
import { UserAvatar } from 'src/Common/Components/UserAvatar';
import { ContentControlButton } from 'src/Common/Components/ContentControl/ContentControlButton';
import { orange, red } from '@mui/material/colors';
import useEkko from 'src/Common/Providers/EkkoProvider';
import useRequest from 'src/Common/Hooks/useRequest';
import useNavigation from 'src/Common/Providers/NavigationProvider';
import { useNavigate } from 'react-router';
import usePagination from 'src/Common/Providers/PaginationProvider';
import { useContentControl } from 'src/Common/Stores/ContentControlProvider';
import { CenteredColumn } from 'src/Common/Components/Stacks';
import ScrollableStack from 'src/Common/Components/ScrollableStack';

// Shared row height keeps scroll indicator chevrons vertically aligned
// across all proportional columns in the users table.
const ROW_MIN_HEIGHT = 48;

export const UserManagementContent = (): React.JSX.Element => {
  const { api, hasPermission, config } = useEkko();
  const { setPanelOpen } = useContentControl();
  const { refresh } = usePagination();
  const { scrollTop } = useNavigation();
  const navigate = useNavigate();

  const [, startSaving] = useRequest<User>();

  const [openDialog, setOpenDialog] = useState<'delete' | 'valid' | undefined>();
  const [editableUser, setEditableUser] = useState<User | undefined>();

  const onDismiss = () => {
    setOpenDialog(undefined);
    setPanelOpen(false);
    setEditableUser(undefined);
  };

  const fetchItems = useCallback(
    (pagination: Pagination): Promise<PaginationResponse<User>> => api.getUsers(pagination, true, true),
    [api],
  );

  const deleteHandler = useCallback((user: User) => api.deleteUser(user), [api, refresh]);

  const saveHandler = useCallback(
    async (user: UserUpdatePayload | UserCreatePayload, password?: string) => {
      if ((user as UserUpdatePayload).id === undefined) {
        // add
        startSaving(
          () => api.addUser(user as UserCreatePayload, password),
          () => {
            enqueueSnackbar('User created.');
            onDismiss();
            refresh();
          },
        );
      } else {
        // update
        startSaving(
          () => api.updateUser(user as UserUpdatePayload, password),
          () => {
            enqueueSnackbar('User updated.');
            onDismiss();
            refresh();
          },
        );
      }
    },
    [api, refresh],
  );

  const onDeleteOk = useCallback(async () => {
    if (editableUser?.id) {
      await deleteHandler(editableUser);
    }
    onDismiss();
  }, [editableUser]);

  const onUpdateOk = useCallback(async () => {
    if (
      !editableUser ||
      !editableUser.id ||
      !editableUser.username ||
      !editableUser.coordinate ||
      !editableUser.email ||
      !editableUser.validUntil ||
      editableUser.validUntil === ''
    ) {
      return;
    }

    const user = {
      id: editableUser.id,
      username: editableUser.username,
      email: editableUser.email,
      coordinate: editableUser.coordinate,
      validUntil: editableUser.validUntil,
    };

    await saveHandler(user);
    onDismiss();
  }, [editableUser, saveHandler]);

  const onChangeValidUntil = useCallback(
    (date: Moment | null) => {
      if (!editableUser || date === null) {
        return;
      }

      editableUser.validUntil = date.format('YYYY-MM-DD');
      setEditableUser(editableUser);
    },
    [editableUser],
  );

  const dialog = useMemo(() => {
    if (!openDialog || editableUser?.id === undefined) {
      return undefined;
    }

    switch (openDialog) {
      case 'delete': {
        return (
          <GenericDialog
            open
            fullWidth
            centered
            timedOkButton
            type="confirmation"
            maxWidth={'sm'}
            title={'Delete User'}
            text={
              <CenteredColumn>
                <Typography>{'Delete User "' + editableUser.username + '"?'}</Typography>
                <Typography style={{ margin: 16 }} variant={'caption'}>
                  This action can not be undone.
                </Typography>
              </CenteredColumn>
            }
            onClose={onDismiss}
            onConfirm={onDeleteOk}
          />
        );
      }
      case 'valid': {
        const validUntil = moment(editableUser.validUntil);
        return (
          <GenericDialog
            open
            type="confirmation"
            title={'Update Valid-Until'}
            text={
              <Stack spacing={2} sx={{ minWidth: 300, pt: 1 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <UserAvatar username={editableUser.username} size="large" />
                  <Stack spacing={0.5}>
                    <Typography fontWeight={600}>{editableUser.username}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {editableUser.email}
                    </Typography>
                  </Stack>
                </Stack>
                <Divider />
                <DatePicker
                  label="Valid-Until"
                  disablePast
                  value={validUntil}
                  onChange={onChangeValidUntil}
                  minDate={moment().add(1, 'd')}
                  sx={{ width: '100%' }}
                />
              </Stack>
            }
            onClose={onDismiss}
            onConfirm={onUpdateOk}
          />
        );
      }
      default:
        return;
    }
  }, [openDialog, editableUser, onUpdateOk]);

  return (
    <ContentControl
      panel={
        <UserPanel
          user={editableUser}
          closeHandler={onDismiss}
          saveHandler={saveHandler}
          passwordEditable={config.api.authMechanism !== AuthMechanisms.OAUTH2}
        />
      }
      dialog={dialog}
      buttons={[
        <ContentControlButton
          key={'add'}
          content={<Add />}
          onClick={() => {
            onDismiss();
            setEditableUser(undefined);
            setPanelOpen(true);
          }}
          tooltip={'Add User'}
          requiredPermission={Permissions.USERS_WRITE}
        />,
      ]}
    >
      <DataTable
        fetchItems={fetchItems}
        maxExpandedActions={3}
        actions={[
          {
            title: 'Roles',
            onClick: (entry: User) => navigate('/mappings/user/' + entry.id),
            icon: <SupervisedUserCircle />,
            disabled: !hasPermission(Permissions.MAPPINGS_READ),
          },
          {
            title: 'Edit',
            icon: <Edit />,
            onClick: (entry: User) => {
              onDismiss();
              scrollTop();
              setEditableUser(entry);
              setPanelOpen(true);
            },
            disabled: (entry: User) => !!entry.automation,
          },
          {
            title: 'Delete',
            icon: <Delete />,
            onClick: (entry: User) => {
              setPanelOpen(false);
              setEditableUser(entry);
              setOpenDialog('delete');
            },
          },
        ]}
        columns={[
          {
            header: 'Username',
            body: (entry: User, searchText) => (
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography fontWeight={600}>
                  {searchText ? dataTableHighlight(entry.username, [searchText]) : entry.username}
                </Typography>
                {!!entry.automation && (
                  <Tooltip title={'automation user'}>
                    <SmartToyOutlined fontSize="small" color="action" />
                  </Tooltip>
                )}
              </Stack>
            ),
            filterField: 'username',
            sortField: 'U.username',
            fitContent: true,
          },
          {
            header: 'E-Mail',
            body: (entry: User, searchText) => (
              <ScrollableStack direction="row" width="100%" minHeight={ROW_MIN_HEIGHT} alignItems="center">
                <Typography
                  component="a"
                  href={`mailto:${entry.email}`}
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.875rem',
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline',
                      color: 'primary.main',
                    },
                  }}
                >
                  {searchText ? dataTableHighlight(entry.email, [searchText]) : entry.email}
                </Typography>
              </ScrollableStack>
            ),
            filterField: 'email',
            sortField: 'U.email',
            proportionalContent: '45%',
          },
          {
            header: 'Coordinate',
            body: (entry: User, searchText?: string) => (
              <ScrollableStack direction="row" width="100%" minHeight={ROW_MIN_HEIGHT} alignItems="center">
                {searchText ? dataTableHighlight(entry.coordinate, [searchText]) : entry.coordinate}
              </ScrollableStack>
            ),
            filterField: 'coordinate',
            sortField: 'Node.materializedPath',
            proportionalContent: '55%',
          },
          {
            header: 'Valid-Until',
            body: (entry: User) => {
              if (entry.automation) {
                return (
                  <Chip
                    label="No expiry"
                    size="small"
                    sx={{
                      backgroundColor: 'action.selected',
                      color: 'text.secondary',
                      fontWeight: 500,
                      fontSize: 11,
                      height: 20,
                    }}
                  />
                );
              }
              const momentDate = moment(entry.validUntil);
              const hoursDiff = momentDate.diff(moment(), 'hours');
              const isExpired = hoursDiff <= 0;
              const isExpiringSoon = hoursDiff > 0 && hoursDiff < 48;

              const relativeTime = momentDate.fromNow();

              return (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography
                    onClick={() => {
                      setEditableUser(entry);
                      setOpenDialog('valid');
                    }}
                    sx={{
                      cursor: 'pointer',
                      borderBottom: '1px dashed',
                      borderColor: 'text.secondary',
                      '&:hover': {
                        borderColor: 'primary.main',
                        color: 'primary.main',
                      },
                    }}
                  >
                    {momentDate.format('DD MMM YYYY')}
                  </Typography>
                  <Chip
                    label={relativeTime}
                    size="small"
                    sx={{
                      backgroundColor: isExpired ? red[500] : isExpiringSoon ? orange[500] : 'action.selected',
                      color: isExpired || isExpiringSoon ? 'white' : 'text.secondary',
                      fontWeight: 500,
                      fontSize: 11,
                      height: 20,
                    }}
                  />
                </Stack>
              );
            },
            fitContent: true,
          },
        ]}
      />
    </ContentControl>
  );
};
